import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
  calculateHeaderGroupHeight,
  calculateHeaderPrecedingHeight,
} from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Last scroll position used for floating header visibility direction changes
   * @type {number | null}
   */
  #lastFloatingScrollTop = null;

  /**
   * Minimum scroll delta before toggling floating header visibility
   * @type {number}
   */
  #floatingVisibilityDeadzone = 8;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    if (event.detail.minimumReached) {
      this.setAttribute('data-nav-overflow', '');
    } else {
      this.removeAttribute('data-nav-overflow');
    }
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #shouldTrackFloatingHeader = () => this.classList.contains('header-float') && this.dataset.floatingHeader === 'true';

  #shouldIgnoreScrollDrivenState = () =>
    document.body.classList.contains('dialog-open') || document.body.dataset.restoringDialogScroll === 'true';

  /**
   * Header state snapshot used while dialogs temporarily lock and restore scroll.
   * @type {{
   *   stickyState?: string;
   *   scrollDirection?: string;
   *   floatState?: string;
   *   floatingVisibility?: string;
   * } | null}
   */
  #dialogStateSnapshot = null;

  #handleDialogOpen = () => {
    this.#dialogStateSnapshot = {
      stickyState: this.dataset.stickyState,
      scrollDirection: this.dataset.scrollDirection,
      floatState: this.dataset.floatState,
      floatingVisibility: this.dataset.floatingVisibility,
    };
  };

  #handleDialogClose = () => {
    if (!this.#dialogStateSnapshot) return;

    requestAnimationFrame(() => {
      if (this.#dialogStateSnapshot?.stickyState) {
        this.dataset.stickyState = this.#dialogStateSnapshot.stickyState;
      }

      if (this.#dialogStateSnapshot?.scrollDirection) {
        this.dataset.scrollDirection = this.#dialogStateSnapshot.scrollDirection;
      }

      if (this.#dialogStateSnapshot?.floatState) {
        this.dataset.floatState = this.#dialogStateSnapshot.floatState;
      }

      if (this.#dialogStateSnapshot?.floatingVisibility) {
        this.dataset.floatingVisibility = this.#dialogStateSnapshot.floatingVisibility;
      }

      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.#lastScrollTop = scrollTop;
      this.#lastFloatingScrollTop = scrollTop;
      this.#dialogStateSnapshot = null;
    });
  };

  #updateFloatingHeaderState = () => {
    if (!this.#shouldTrackFloatingHeader()) return;

    if (this.#shouldIgnoreScrollDrivenState()) {
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.#lastFloatingScrollTop = scrollTop;
      return;
    }

    const precedingHeight = parseFloat(document.body.style.getPropertyValue('--header-preceding-height')) || 0;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const nextFloatState = scrollTop > precedingHeight ? 'docked' : 'floating';
    const currentFloatingVisibility = this.dataset.floatingVisibility ?? 'visible';
    let nextFloatingVisibility = currentFloatingVisibility;

    if (this.#lastFloatingScrollTop === null) {
      nextFloatingVisibility = 'visible';
      this.#lastFloatingScrollTop = scrollTop;
    } else if (nextFloatState !== 'docked') {
      nextFloatingVisibility = 'visible';
      this.#lastFloatingScrollTop = scrollTop;
    } else {
      const scrollDelta = scrollTop - this.#lastFloatingScrollTop;

      if (scrollDelta > this.#floatingVisibilityDeadzone) {
        nextFloatingVisibility = 'hidden';
        this.#lastFloatingScrollTop = scrollTop;
      } else if (scrollDelta < this.#floatingVisibilityDeadzone * -1) {
        nextFloatingVisibility = 'visible';
        this.#lastFloatingScrollTop = scrollTop;
      }
    }

    if (this.dataset.floatState !== nextFloatState) {
      this.dataset.floatState = nextFloatState;
    }

    if (this.dataset.floatingVisibility !== nextFloatingVisibility) {
      this.dataset.floatingVisibility = nextFloatingVisibility;
    }
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
      this.#updateFloatingHeaderState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    if (this.#shouldIgnoreScrollDrivenState()) {
      this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
      return;
    }

    const precedingHeight = parseFloat(document.body.style.getPropertyValue('--header-preceding-height')) || 0;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const hasScrolledPastHeaderFootprint = scrollTop > precedingHeight + this.offsetHeight;

      if (!hasScrolledPastHeaderFootprint || isAtTop) {
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'none';
        this.dataset.stickyState = 'idle';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    setHeaderMenuStyle();
    this.#lastFloatingScrollTop = null;
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');
    }

    if (stickyMode === 'scroll-up' || stickyMode === 'always' || this.#shouldTrackFloatingHeader()) {
      document.addEventListener('scroll', this.#handleWindowScroll);
    }
    document.addEventListener('dialog:open', this.#handleDialogOpen);
    document.addEventListener('dialog:close', this.#handleDialogClose);

    this.#updateFloatingHeaderState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#lastFloatingScrollTop = null;
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.removeEventListener('dialog:open', this.#handleDialogOpen);
    document.removeEventListener('dialog:close', this.#handleDialogClose);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.#dialogStateSnapshot = null;
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = /** @type {HTMLElement | null } */ (document.querySelector('#header-group'));

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  /**
   * Avoid triggering style recalculation when the custom property value is unchanged.
   * @param {HTMLElement} element
   * @param {string} property
   * @param {string} value
   */
  const setStylePropertyIfChanged = (element, property, value) => {
    if (element.style.getPropertyValue(property) !== value) {
      element.style.setProperty(property, value);
    }
  };

  /**
   * Avoid attribute writes when the next value matches the current one.
   * @param {HTMLElement} element
   * @param {string} name
   * @param {string} value
   */
  const setDataAttributeIfChanged = (element, name, value) => {
    if (element.dataset[name] !== value) {
      element.dataset[name] = value;
    }
  };

  const syncHeaderGroupCustomProperties = () => {
    if (!(header instanceof HTMLElement) || !headerGroup) return;

    const headerGroupHeight = Math.round(calculateHeaderGroupHeight(header, headerGroup));
    const headerPrecedingHeight = Math.round(calculateHeaderPrecedingHeight(header, headerGroup));
    const headerTopRow = /** @type {HTMLElement | null } */ (header.querySelector('.header__row--top'));
    const headerGroupHeightValue = `${headerGroupHeight}px`;
    const headerPrecedingHeightValue = `${headerPrecedingHeight}px`;

    setStylePropertyIfChanged(document.body, '--header-group-height', headerGroupHeightValue);
    setStylePropertyIfChanged(document.body, '--header-preceding-height', headerPrecedingHeightValue);

    if (headerTopRow) {
      requestAnimationFrame(() => {
        setStylePropertyIfChanged(header, '--top-row-height', `${headerTopRow.offsetHeight}px`);
      });
    }

    if (header.classList.contains('header-float') && header.dataset.floatingHeader === 'true') {
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      const nextFloatState = scrollTop > headerPrecedingHeight ? 'docked' : 'floating';

      setDataAttributeIfChanged(header, 'floatState', nextFloatState);

      if (!header.dataset.floatingVisibility || nextFloatState !== 'docked') {
        setDataAttributeIfChanged(header, 'floatingVisibility', 'visible');
      }
    }
  };

  // Update header group height on resize of any child
  if (headerGroup && header instanceof HTMLElement) {
    const resizeObserver = new ResizeObserver(() => {
      syncHeaderGroupCustomProperties();
    });

    syncHeaderGroupCustomProperties();

    resizeObserver.observe(header);

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }

          syncHeaderGroupCustomProperties();
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
